import { styled } from "@mui/material/styles";
import { grey } from "designSystem/styles/color";
import { ComponentItem } from "designSystem/types/component-item-types";
import { FlexRowCenterProps } from "designSystem/components/flex/FlexRowCenter";
import { Button, Chip, ChipArray, FlexRowCenter, Grid, Text } from "designSystem/index";
import Spec from "csi/core/Spec";
import { observer } from "mobx-react-lite";
import React, { Fragment, FunctionComponent } from "react";
import { RULE_FILTER_ALL_ITEMS } from "../attributeRule/RuleService";
import { AttributeSpecAndValues, CnlsAndValues, ComponentFilterItem } from "../common";
import FilterStore from "./FilterStore";

const PREFIX = "FilterPillGroup";

const classes = {
	chipRoot: `${PREFIX}chipRoot`,
	filterContainer: `${PREFIX}filterContainer`,
};

const StyledFlexRowCenter = styled(FlexRowCenter)(({
	theme,
}) => ({
	[`& .${classes.chipRoot}`]: {
		backgroundColor: grey[300],
		borderRadius: 15,
		minHeight: 28,
		paddingLeft: theme.spacing(2),
		paddingRight: theme.spacing(1),
	},

	[`&.${classes.filterContainer}`]: {
		marginBottom: theme.spacing(3),
	},
}));

interface FilterGroupProps extends FlexRowCenterProps {
	/** callback for delete filter value */
	deleteFilter: (item: ComponentItem, spec: Spec, cnlsAndValues: CnlsAndValues) => void;

	/** call back for reset filter */
	resetFilter: () => void;

	/** node type, either domain type or the type along the matrix path */
	type: string;

	/** spec and corresponding values */
	values: AttributeSpecAndValues[];
}

const FilterGroup: FunctionComponent<FilterGroupProps> = (props) => {
	const {
		deleteFilter,
		resetFilter,
		type,
		values,
		...otherProps
	} = props;
	return (
		<StyledFlexRowCenter {...otherProps}>
			<Grid
				alignItems="center"
				container
				spacing={2}
			>
				<Grid item>
					<Text
						bold
						text={type}
					/>
				</Grid>
				{values.map(({ spec, values }, i) => (
					<Grid item key={i}>
						<ChipArray<ComponentFilterItem>
							items={values}
							name={spec.name}
							onDeleteItem={(item) => deleteFilter(item, spec, { values: [item.value], cnls: [...item.cnls] })}
							variant="outlined"
						/>
					</Grid>
				))}
				<Grid item>
					<Button
						design="link"
						onClick={resetFilter}
						res="ClearAll"
					/>
				</Grid>
			</Grid>
		</StyledFlexRowCenter>
	);
};

interface FilterPillGroupProps {
	/** store for this component */
	store: FilterStore;
}

const FilterPillGroup: FunctionComponent<FilterPillGroupProps> = observer((props) => {
	const { store } = props;

	const {
		filterSpecAndValuesForDomainType,
		filterSpecAndValuesForNodeInMatrixPath,
		matrixPath,
		nodeTypeAlongMatrixPath,
		domainType,
		deleteFilter,
		resetFilter,
		quickFilterId,
		resetQuickFilter,
	} = store || {};

	return (
		<>
			{quickFilterId && RULE_FILTER_ALL_ITEMS !== quickFilterId && (
				<StyledFlexRowCenter className={classes.filterContainer}>
					<Grid
						alignItems="center"
						container
						spacing={2}
					>
						<Grid item>
							<Text
								bold
								res="RuleQuickFilterSection"
							/>
						</Grid>
						<Grid item>
							<Chip
								className={classes.chipRoot}
								onDelete={resetQuickFilter}
								res={quickFilterId}
								variant="outlined"
							/>
						</Grid>
					</Grid>
				</StyledFlexRowCenter>
			)}
			{filterSpecAndValuesForDomainType?.length > 0 && (
				<FilterGroup
					className={classes.filterContainer}
					deleteFilter={deleteFilter}
					resetFilter={() => resetFilter()}
					type={domainType}
					values={filterSpecAndValuesForDomainType}
				/>
			)}
			{filterSpecAndValuesForNodeInMatrixPath?.length > 0 && (
				<FilterGroup
					className={classes.filterContainer}
					deleteFilter={deleteFilter}
					resetFilter={() => resetFilter(matrixPath)}
					type={nodeTypeAlongMatrixPath}
					values={filterSpecAndValuesForNodeInMatrixPath}
				/>
			)}
		</>
	);
});

export default FilterPillGroup;